import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { Pagination } from "../../components/ui/pagination/Pagination";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { LineChart } from "../../components/ui/lineChart/LineChart";
import { BarChart } from "../../components/ui/barChart/BarChart";
import { useModal } from "../../components/ui/modal/ModalProvider";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { MOCK_DATA, useMockDataVersion } from "../../data/mockDataStore";
import { datetime, money } from "../../utils/format";

const TABS = [
  { key: "dashboard", label: "Vue d'ensemble" },
  { key: "balances", label: "Soldes partenaires" },
  { key: "transactions", label: "Transactions" },
  { key: "ca", label: "Chiffre d'affaires" },
];

const TX_PAGE_SIZE = 8;

export function Finance() {
  useMockDataVersion();
  const navigate = useNavigate();
  const { balances } = MOCK_DATA;
  const [tab, setTab] = useState("dashboard");

  const soldeGlobal = balances.reduce((s, b) => s + b.solde, 0);
  const creances = balances.filter((b) => b.solde < 0).reduce((s, b) => s + Math.abs(b.solde), 0);
  const aVerser = balances.filter((b) => b.solde > 0).reduce((s, b) => s + b.solde, 0);
  const ca = 4870000;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/dashboard")}>Dashboard</a>
            <i className="fa-solid fa-chevron-right"></i>Finance
          </div>
          <h1 className="page-title">Finance &amp; portefeuille partenaires</h1>
          <div className="page-sub">Soldes, transactions et chiffre d'affaires</div>
        </div>
        <div className="page-actions">
          <button className="btn"><i className="fa-solid fa-download"></i> Exporter le rapport</button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi"><div className="kpi-label"><i className="fa-solid fa-scale-balanced"></i> Solde global</div><div className="kpi-value">{money(soldeGlobal)}</div><span className={`kpi-delta ${soldeGlobal >= 0 ? "up" : "down"}`}>{soldeGlobal >= 0 ? "Positif" : "Négatif"}</span></div>
        <div className="kpi"><div className="kpi-label"><i className="fa-solid fa-arrow-down"></i> À recouvrer</div><div className="kpi-value">{money(creances)}</div><span className="kpi-delta down">{balances.filter((b) => b.solde < 0).length} partenaire(s)</span></div>
        <div className="kpi"><div className="kpi-label"><i className="fa-solid fa-arrow-up"></i> À verser</div><div className="kpi-value">{money(aVerser)}</div><span className="kpi-delta up">{balances.filter((b) => b.solde > 0).length} partenaire(s)</span></div>
        <div className="kpi"><div className="kpi-label"><i className="fa-solid fa-chart-line"></i> CA trimestre</div><div className="kpi-value">{money(ca)}</div><span className="kpi-delta up"><i className="fa-solid fa-arrow-up"></i> +18,3%</span></div>
      </div>

      <div className="card">
        <div className="tabs">
          {TABS.map((t) => (
            <div key={t.key} className={`tab${tab === t.key ? " active" : ""}`} data-testid={`tab-fin-${t.key}`} onClick={() => setTab(t.key)}>
              {t.label}
            </div>
          ))}
        </div>
        <div id="finContent">
          {tab === "dashboard" ? <FinanceDashboardTab /> : null}
          {tab === "balances" ? <FinanceBalancesTab /> : null}
          {tab === "transactions" ? <FinanceTransactionsTab /> : null}
          {tab === "ca" ? <FinanceCaTab /> : null}
        </div>
      </div>
    </>
  );
}

function FinanceDashboardTab() {
  const monthLabels = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  const vals = [3200, 3450, 3800, 4100, 3900, 4300, 4600, 4200, 4870, 4500, 4820, 5100];
  const top = useMemo(() => [...MOCK_DATA.balances].sort((a, b) => a.solde - b.solde).slice(0, 5), []);
  const negatives = top.filter((b) => b.solde < 0);

  return (
    <div className="grid grid-12" style={{ padding: 16, gap: 16 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Évolution du CA (K DA)</div>
        <div className="chart" style={{ height: 260 }}>
          <BarChart values={vals} labels={monthLabels} />
        </div>
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Partenaires — plus importantes créances</div>
        {negatives.length === 0 ? (
          <EmptyState title="Aucune créance" sub="Tous les soldes sont sains." />
        ) : (
          negatives.map((b) => (
            <div key={b.etabId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 8 }}>
              <div><div className="strong">{b.etab}</div><div className="muted">{b.mouvements} mouvements</div></div>
              <div className="mono" style={{ color: "var(--danger)", fontWeight: 700 }}>{money(b.solde)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FinanceBalancesTab() {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { toast } = useToast();

  const openRegularize = () => {
    openModal({
      title: "Régulariser le solde",
      body: (
        <>
          <div className="form-row">
            <label className="label">Méthode</label>
            <select className="input">
              <option>Déduction sur futures réservations</option>
              <option>Remboursement direct du partenaire</option>
            </select>
          </div>
          <div className="form-row"><label className="label">Montant</label><input className="input mono" placeholder="0" /></div>
          <div className="form-row"><label className="label">Commentaire</label><textarea className="textarea" placeholder="Motif de la régularisation…"></textarea></div>
        </>
      ),
      footer: (
        <>
          <button className="btn" onClick={closeModal}>Annuler</button>
          <button className="btn btn-primary" onClick={() => { closeModal(); toast("Régularisation enregistrée", "success"); }}>Enregistrer</button>
        </>
      ),
    });
  };

  return (
    <div>
      <DataTable
        columns={[
          { label: "Établissement", render: (b) => <a onClick={() => navigate("/etablissements/" + b.etabId)} style={{ color: "var(--primary)", cursor: "pointer", fontWeight: 600 }}>{b.etab}</a> },
          {
            label: "Solde",
            render: (b) => {
              const cls = b.solde > 0 ? "positive" : b.solde < 0 ? "negative" : "zero";
              const icoBg = b.solde > 0 ? "var(--success)" : b.solde < 0 ? "var(--danger)" : "var(--neutral)";
              const ico = b.solde > 0 ? "fa-arrow-up" : b.solde < 0 ? "fa-arrow-down" : "fa-equals";
              return (
                <div className={`balance ${cls}`} style={{ display: "inline-flex", padding: "4px 10px" }}>
                  <span className="ico" style={{ background: icoBg, color: "#fff" }}><i className={`fa-solid ${ico}`} style={{ fontSize: 10 }}></i></span>
                  <span className="mono">{money(b.solde)}</span>
                </div>
              );
            },
          },
          {
            label: "État",
            render: (b) =>
              b.solde > 0 ? (
                <span className="badge success"><i className="fa-solid fa-arrow-up"></i> À verser</span>
              ) : b.solde < 0 ? (
                <span className="badge danger"><i className="fa-solid fa-arrow-down"></i> À recouvrer</span>
              ) : (
                <span className="badge neutral">Neutre</span>
              ),
          },
          { label: "Mouvements", render: (b) => <span className="mono">{b.mouvements}</span> },
          { label: "", render: (b) => <div className="actions-cell"><button className="btn btn-sm" data-testid={`regularize-${b.etabId}`} onClick={openRegularize}><i className="fa-solid fa-scale-balanced"></i> Régulariser</button></div> },
        ]}
        rows={MOCK_DATA.balances}
        rowTestIdPrefix="bal"
      />
    </div>
  );
}

const TX_TYPES = ["Encaissement", "Paiement partenaire", "Versement partenaire", "Remboursement client"];

function FinanceTransactionsTab() {
  const [etab, setEtab] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);

  const tx = useMemo(() => {
    return MOCK_DATA.transactions.filter((t) => {
      if (etab && t.etab !== MOCK_DATA.etablissements.find((e) => e.id === etab)?.nom) return false;
      if (type && t.type !== type) return false;
      return true;
    });
  }, [etab, type]);

  const paged = tx.slice((page - 1) * TX_PAGE_SIZE, page * TX_PAGE_SIZE);

  return (
    <>
      <div className="toolbar">
        <select className="select" data-testid="tx-etab" value={etab} onChange={(e) => { setEtab(e.target.value); setPage(1); }}>
          <option value="">Tous les établissements</option>
          {MOCK_DATA.etablissements.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
        </select>
        <select className="select" data-testid="tx-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
          <option value="">Tous les types</option>
          {TX_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="filter-chips"><span className="badge outline">{tx.length} transactions</span></div>
      </div>
      <DataTable
        columns={[
          { label: "Date", render: (t) => datetime(t.date) },
          { label: "Référence", render: (t) => <span className="mono strong">{t.id}</span> },
          { label: "Réservation", render: (t) => <div className="mono">{t.resaId}<div className="muted mono">{t.voyageRef}</div></div> },
          { label: "Établissement", render: (t) => t.etab },
          { label: "Type", render: (t) => <span className="badge outline">{t.type}</span> },
          { label: "Montant", render: (t) => <span className="mono strong" style={{ color: t.montant < 0 ? "var(--danger)" : "var(--success)" }}>{t.montant < 0 ? "" : "+"}{money(t.montant)}</span> },
          { label: "Solde après", render: (t) => <span className="mono">{money(t.solde)}</span> },
          { label: "Statut", render: (t) => <StatusBadge statut={t.statut} /> },
        ]}
        rows={paged}
        rowTestIdPrefix="tx"
      />
      <Pagination total={tx.length} page={page} pageSize={TX_PAGE_SIZE} onPageChange={setPage} />
    </>
  );
}

function FinanceCaTab() {
  return (
    <>
      <div className="toolbar">
        <select className="select" data-testid="ca-partner">
          <option>Tous les partenaires</option>
          {MOCK_DATA.etablissements.map((e) => <option key={e.id}>{e.nom}</option>)}
        </select>
        <select className="select" data-testid="ca-period"><option>Mois</option><option>Trimestre</option><option>Année</option><option>Personnalisée</option></select>
      </div>
      <div style={{ padding: 16 }}>
        <div className="grid grid-3" style={{ gap: 12 }}>
          <div className="kpi"><div className="kpi-label">CA total</div><div className="kpi-value">{money(12480000)}</div><span className="kpi-delta up"><i className="fa-solid fa-arrow-up"></i> +14,2%</span></div>
          <div className="kpi"><div className="kpi-label">Panier moyen</div><div className="kpi-value">{money(87500)}</div><span className="kpi-delta up"><i className="fa-solid fa-arrow-up"></i> +3,8%</span></div>
          <div className="kpi"><div className="kpi-label">Transactions</div><div className="kpi-value">142</div><span className="kpi-delta neutral">30 j</span></div>
        </div>
        <div style={{ height: 16 }}></div>
        <div className="chart" style={{ height: 300 }}>
          <LineChart values={[320, 340, 290, 380, 410, 460, 510, 540, 600, 620, 680, 720]} />
        </div>
      </div>
    </>
  );
}

export default Finance;
